/**
 * tokenManager.ts
 *
 * Single source of truth for the Auth0 Access Token used by the Outlook
 * add-in. Replaces the previous pattern of writing the raw token to
 * localStorage under several different keys (authToken, authToken1,
 * loggedUsers[].token) from LoginComponents.tsx / helpers.tsx / App.tsx.
 *
 * Design goals:
 *  - The Access Token itself is kept in memory only (module-level
 *    variable). It is never written to localStorage.
 *  - The Auth0 Refresh Token is NEVER sent to, or stored in, the browser.
 *    It lives only in the Public API's database (Auth0UserSessions).
 *  - The add-in instead holds a short opaque "session handle" returned by
 *    the Public API after login. That handle is meaningless on its own
 *    (Auth0 doesn't accept it) - the API is the only thing that can turn
 *    it into a new Access Token. It is stored in sessionStorage (cleared
 *    when the Outlook window/tab closes), which is strictly better than
 *    localStorage (not persisted indefinitely, not shared across every
 *    tab/profile on the machine) while still surviving a task pane reload.
 *  - Because index.html loads both the React bundle and the legacy
 *    (non-React) message-send script in the SAME shared runtime (the
 *    manifest's WebViewRuntime.Url / JSRuntime.Url / Commands.Url all
 *    point at index.html), this module also exposes itself on
 *    `window.ClarraAuth` so src/legacy/messageSendHandlers.js can reuse
 *    the exact same in-memory token/refresh logic instead of reading
 *    localStorage directly.
 *
 * ============================================================================
 * FIX: Session Handle Persistence via Office.roamingSettings (2026-07-29)
 * ============================================================================
 * MARKER: START-SESSION-PERSISTENCE-FIX
 *
 * Added: Office.context.roamingSettings as a secure fallback for session
 * handle recovery when sessionStorage is cleared on app reopen.
 *
 * Rationale:
 *  - sessionStorage is cleared when Outlook closes/reopens on all platforms
 *  - Users were forced to re-login despite having valid sessions on server
 *  - Office.roamingSettings is:
 *    ✓ Per-user encrypted (Azure-backed, not localStorage)
 *    ✓ Survives app reopen
 *    ✓ Works across Web, Classic, and New Outlook
 *  - This is NOT localStorage (security vulnerability we fixed previously)
 *  - This IS Microsoft Office's secure persistence API
 *
 * Security Guarantees:
 *  ✓ Session handle is opaque (meaningless without server validation)
 *  ✓ Real refresh token NEVER stored on client
 *  ✓ Refresh token only accessible to server (encrypted in DB)
 *  ✓ Handle rotation on each refresh prevents reuse
 *  ✓ Office.roamingSettings managed by Microsoft, not browser
 *  ✓ Per-user isolation enforced by Office platform
 *
 * To ROLLBACK this fix:
 *  1. Search for: MARKER: START-SESSION-PERSISTENCE-FIX
 *  2. Delete all code between START and END markers
 *  3. Uncomment lines marked "ORIGINAL:" (if any)
 *  4. Revert to sessionStorage-only behavior (users must re-login on app reopen)
 * ============================================================================
 */

import { API_BASE_URL } from "../config/config";

const SESSION_HANDLE_KEY = "clarra_session_handle"; // sessionStorage only - NOT the refresh token

// MARKER: START-SESSION-PERSISTENCE-FIX
/** Office.roamingSettings key for persistent session handle recovery */
const ROAMING_SETTINGS_HANDLE_KEY = "clarra_session_handle_persist";

/** Stores the user ID for session recovery (classic Outlook path) */
const ROAMING_SETTINGS_USER_ID_KEY = "clarra_recovery_user_id";
// MARKER: END-DECLARATION-SESSION-PERSISTENCE-FIX

const REFRESH_SKEW_MS = 5 * 60_000; // refresh 5 minutes before actual expiry

interface DecodedAccessToken {
  exp?: number;
  [claim: string]: unknown;
}

interface SessionResponse {
  accessToken: string;
  expiresIn: number; // seconds
  sessionHandle: string;
}

let inMemoryAccessToken: string | null = null;
let inMemoryExpiresAtMs = 0;
let refreshInFlight: Promise<string | null> | null = null;

function decodeJwt(token: string): DecodedAccessToken | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function setAccessToken(token: string) {
  inMemoryAccessToken = token;
  const decoded = decodeJwt(token);
  inMemoryExpiresAtMs = decoded?.exp ? decoded.exp * 1000 : Date.now() + 5 * 60_000;
}

// MARKER: START-SESSION-PERSISTENCE-FIX
/**
 * Get session handle synchronously (checks sessionStorage only, no recovery)
 * Used for quick non-blocking checks.
 * For full recovery (including Office.roamingSettings), use getSessionHandleWithRecovery()
 */
export function getSessionHandle(): string | null {
  try {
    return sessionStorage.getItem(SESSION_HANDLE_KEY);
  } catch {
    return null;
  }
}
// MARKER: END-SESSION-PERSISTENCE-FIX

// MARKER: START-SESSION-PERSISTENCE-FIX
/**
 * Asynchronously get session handle with fallback to Office.roamingSettings.
 *
 * Behavior:
 *  1. Check sessionStorage first (fastest path)
 *  2. If not found, check Office.roamingSettings (recovery after app reopen)
 *  3. If found in roaming, restore to sessionStorage and return
 *  4. If not found anywhere, return null
 *
 * Security Note:
 *  - Office.roamingSettings is per-user, encrypted by Microsoft Office platform
 *  - Session handle is opaque (requires server validation to be useful)
 *  - Real refresh token is never stored on client
 *
 * @returns Promise<string | null> - Session handle or null if not available
 */
async function getSessionHandleWithRecovery(): Promise<string | null> {
  // Step 1: Try sessionStorage (fastest)
  try {
    const sessionHandle = sessionStorage.getItem(SESSION_HANDLE_KEY);
    if (sessionHandle) {
      return sessionHandle;
    }
  } catch (err) {
    console.warn("[tokenManager] Failed to read sessionStorage", err);
  }

  // Step 2: Try Office.roamingSettings (recovery after app reopen)
  try {
    if (typeof Office === "undefined") {
      // Not running in Office context; can't use roamingSettings
      return null;
    }

    if (!Office.context || !Office.context.roamingSettings) {
      // Office context not available yet
      return null;
    }

    const roamingHandle = Office.context.roamingSettings.get(ROAMING_SETTINGS_HANDLE_KEY);
    if (roamingHandle && typeof roamingHandle === "string") {
      // Step 3: Restore to sessionStorage for consistency on future calls
      try {
        sessionStorage.setItem(SESSION_HANDLE_KEY, roamingHandle);
      } catch {
        // sessionStorage unavailable; use roaming version directly
      }

      console.log("[tokenManager] Session handle recovered from Office.roamingSettings");
      return roamingHandle;
    }
  } catch (err) {
    console.warn("[tokenManager] Failed to access Office.roamingSettings", err);
    // Fall through to return null; this is not a fatal error
  }

  return null;
}
// MARKER: END-SESSION-PERSISTENCE-FIX

// MARKER: START-SESSION-PERSISTENCE-FIX
/**
 * Store user ID from decoded token (used for recovery in classic Outlook).
 * Called after successful login.
 */
export function setRecoveryUserId(userId: string): void {
  try {
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      Office.context.roamingSettings.set(ROAMING_SETTINGS_USER_ID_KEY, userId);
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.warn("[tokenManager] Failed to save recovery user ID:", result.error);
        } else {
          console.log("[tokenManager] Recovery user ID persisted");
        }
      });
    }
  } catch (err) {
    console.warn("[tokenManager] Exception while saving recovery user ID:", err);
  }
}

/**
 * Get recovery user ID from roaming settings (for classic Outlook recovery).
 */
export function getRecoveryUserId(): string | null {
  try {
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      const userId = Office.context.roamingSettings.get(ROAMING_SETTINGS_USER_ID_KEY);
      if (userId && typeof userId === "string") {
        return userId;
      }
    }
  } catch (err) {
    console.warn("[tokenManager] Failed to get recovery user ID:", err);
  }
  return null;
}

/**
 * Set session handle in both sessionStorage and Office.roamingSettings.
 * Called after login or recovery to store the opaque session reference.
 */
export function setSessionHandle(handle: string) {
  try {
    sessionStorage.setItem(SESSION_HANDLE_KEY, handle);
  } catch {
    // sessionStorage unavailable (rare in Office WebViews) - session
    // simply won't survive a task-pane reload; the user re-authenticates.
  }

  // Also persist to Office.roamingSettings for recovery across app reopens
  try {
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      Office.context.roamingSettings.set(ROAMING_SETTINGS_HANDLE_KEY, handle);
      // Save asynchronously (don't block on this)
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.warn("[tokenManager] Failed to save session handle to roamingSettings:", result.error);
        } else {
          console.log("[tokenManager] Session handle persisted to Office.roamingSettings");
        }
      });
    }
  } catch (err) {
    console.warn("[tokenManager] Exception while persisting session handle:", err);
    // Non-fatal; sessionStorage version is still stored
  }
}
// MARKER: END-SESSION-PERSISTENCE-FIX

function clearSessionHandle() {
  try {
    sessionStorage.removeItem(SESSION_HANDLE_KEY);
  } catch {
    /* noop */
  }

  // MARKER: START-SESSION-PERSISTENCE-FIX
  // Also clear from Office.roamingSettings on logout
  try {
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      Office.context.roamingSettings.remove(ROAMING_SETTINGS_HANDLE_KEY);
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.warn("[tokenManager] Failed to clear session handle from roamingSettings:", result.error);
        } else {
          console.log("[tokenManager] Session handle cleared from Office.roamingSettings");
        }
      });
    }
  } catch (err) {
    console.warn("[tokenManager] Exception while clearing roamingSettings:", err);
    // Non-fatal; local state is already cleared
  }
  // MARKER: END-SESSION-PERSISTENCE-FIX
}

/**
 * Call this once, right after the Auth0 login dialog redirects back with an
 * authorization `code` (Authorization Code + PKCE flow - see pkce.ts). The
 * add-in never calls Auth0's /oauth/token endpoint itself; it hands the
 * code + PKCE verifier to the Public API, which does the code-for-token
 * exchange server-to-server. That means the Auth0 refresh token is
 * produced on the server and NEVER transits the browser at all, not even
 * transiently - only the resulting short-lived access token + opaque
 * session handle come back.
 */
export async function establishSession(params: {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}): Promise<string> {
  const response = await fetch(`${API_BASE_URL}AuthSession/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      code: params.code,
      codeVerifier: params.codeVerifier,
      redirectUri: params.redirectUri,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to establish session (${response.status})`);
  }

  const data: SessionResponse = await response.json();
  setAccessToken(data.accessToken);
  setSessionHandle(data.sessionHandle);
  return data.accessToken;
}

/**
 * Ask the Public API for a new Access Token using the stored session
 * handle. The API looks up the encrypted refresh token, calls Auth0
 * server-to-server, and returns a fresh access token. The real refresh
 * token never leaves the API.
 */
type SilentRefreshFn = () => Promise<string | null>;
let silentRefreshFn: SilentRefreshFn | null = null;

/**
 * Registers a fallback silent-refresh function for the "modern host"
 * login path (LoginComponents.tsx's loginWithPopup/getAccessTokenSilently
 * branch via @auth0/auth0-react). That SDK manages its own refresh token
 * internally and never hands it to this module (by design - see
 * MIGRATION_NOTES.md), so there is no session handle to refresh through
 * the Public API for that path. App.tsx calls this once with a wrapper
 * around useAuth0()'s getAccessTokenSilently so tokenManager can still
 * transparently renew the token when it's the one apiClient is using.
 */
export function registerSilentRefresh(fn: SilentRefreshFn): void {
  silentRefreshFn = fn;
}

/**
 * Used by the "modern host" login path to hand tokenManager an access
 * token it already obtained from @auth0/auth0-react, so apiClient (which
 * only ever reads from this module) has something to attach to requests.
 * No session handle is created for this path - see registerSilentRefresh.
 */
export function setAccessTokenDirect(token: string): void {
  setAccessToken(token);
}

async function refreshAccessToken(): Promise<string | null> {
  // MARKER: START-SESSION-PERSISTENCE-FIX
  // Use async recovery (checks roamingSettings if needed)
  const handle = await getSessionHandleWithRecovery();
  // MARKER: END-SESSION-PERSISTENCE-FIX

  if (handle) {
    const response = await fetch(`${API_BASE_URL}AuthSession/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionHandle: handle }),
    });

    if (!response.ok) {
      // 401/404 here means the refresh token was revoked/expired server-side
      // (or reuse-detection tripped). There is no way to silently recover -
      // the user must sign in again.
      clearSessionHandle();
      inMemoryAccessToken = null;
      inMemoryExpiresAtMs = 0;
      return null;
    }

    const data: SessionResponse = await response.json();
    setAccessToken(data.accessToken);
    setSessionHandle(data.sessionHandle); // handle rotates with each refresh
    return data.accessToken;
  }

  if (silentRefreshFn) {
    try {
      const token = await silentRefreshFn();
      if (token) {
        setAccessToken(token);
        return token;
      }
    } catch {
      // fall through to null below
    }
  }

  return null;
}

/**
 * Main entry point used by apiClient.ts (and by legacy vanilla JS via
 * window.ClarraAuth.getAccessToken()). Returns a currently-valid access
 * token, transparently refreshing it first if it's expired or about to
 * expire. Concurrent callers share a single in-flight refresh so a burst
 * of API calls doesn't trigger a burst of refresh requests.
 */
export async function getAccessToken(): Promise<string | null> {
  const stillValid =
    inMemoryAccessToken && Date.now() < inMemoryExpiresAtMs - REFRESH_SKEW_MS;

  if (stillValid) {
    return inMemoryAccessToken;
  }

  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

/** Called by the response interceptor after a live 401 from the API. */
export async function forceRefresh(): Promise<string | null> {
  inMemoryExpiresAtMs = 0; // treat current token as dead
  return getAccessToken();
}

export function hasSession(): boolean {
  // True either for the classic-Outlook/AuthSession-brokered path (opaque
  // handle in sessionStorage) or the modern-host path (auth0-react token
  // handed in directly via setAccessTokenDirect, no handle involved).
  return !!getSessionHandle() || !!inMemoryAccessToken;
}

export async function clearSession(): Promise<void> {
  // MARKER: START-SESSION-PERSISTENCE-FIX
  // Get handle with recovery (in case it needs to be revoked)
  const handle = await getSessionHandleWithRecovery();
  // MARKER: END-SESSION-PERSISTENCE-FIX

  inMemoryAccessToken = null;
  inMemoryExpiresAtMs = 0;
  clearSessionHandle();

  if (handle) {
    try {
      await fetch(`${API_BASE_URL}AuthSession/revoke`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionHandle: handle }),
      });
    } catch {
      // best-effort revoke; local state is already cleared either way
    }
  }
}

/** Non-security helper kept for parity with the old extractTenantIdFromToken(). */
export function getDecodedAccessToken(): DecodedAccessToken | null {
  return inMemoryAccessToken ? decodeJwt(inMemoryAccessToken) : null;
}

// Expose to the legacy, non-bundled inline script (src/legacy/messageSendHandlers.js)
// which runs in the same shared Office runtime as this React bundle.
declare global {
  interface Window {
    ClarraAuth?: {
      getAccessToken: typeof getAccessToken;
      getDecodedAccessToken: typeof getDecodedAccessToken;
      hasSession: typeof hasSession;
    };
  }
}

if (typeof window !== "undefined") {
  window.ClarraAuth = { getAccessToken, getDecodedAccessToken, hasSession };
}
